import { Component, Input, OnInit, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormBuilder, FormGroup, ReactiveFormsModule, AbstractControl, ValidationErrors, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { CommonModule } from '@angular/common';
import { Observable, map, catchError, of, tap, finalize } from 'rxjs';
import { RegisterService } from '../../services/register.service';
import { AddressLine, MeterDetails } from '../../models/registration.models';
import { FileInputComponent } from '../file-upload.component';

@Component({
  selector: 'app-meter-selection',
  templateUrl: './meter-selection.component.html',
  styleUrls: ['./meter-selection.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatRadioModule,
    MatButtonModule,
    MatSelectModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatCardModule,
    MatChipsModule,
    FileInputComponent
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MeterSelectionComponent),
      multi: true
    }
  ]
})
export class MeterSelectionComponent implements OnInit, ControlValueAccessor {
  @Input() multiple = false;

  meterForm: FormGroup;
  addressList: AddressLine[] = [];
  selectedMeters: MeterDetails[] = [];
  fetchingMeterDetails = false;
  selectedFile: File | null = null;

  private onChange = (value: MeterDetails[] | File | null) => {};
  private onTouched = () => {};

  constructor(private fb: FormBuilder, private regService: RegisterService) {
    this.meterForm = this.fb.group({
      searchType: ['postcode', Validators.required],
      postcodeSearch: this.fb.group({
        postcode: ['', { validators: [Validators.required, Validators.minLength(6)]}],
        address: ['', Validators.required],
      }),
      mpanSearch: this.fb.group({
        mpan: ['', Validators.required],
      }),
      fileUpload: this.fb.group({
        file: ['', Validators.required]
      })
    });
  }

  test() {
    console.log('Test button clicked', this.meterForm);
  }

  ngOnInit() {
    // Disable MPAN and file upload search initially
    this.meterForm.get('mpanSearch')?.disable();
    this.meterForm.get('fileUpload')?.disable();

    // Watch for search type changes
    this.meterForm.get('searchType')?.valueChanges.subscribe(val => {
      if (val === 'postcode') {
        this.meterForm.get('mpanSearch')?.disable();
        this.meterForm.get('fileUpload')?.disable();
        this.meterForm.get('postcodeSearch')?.reset();
        this.meterForm.get('postcodeSearch')?.enable();
        this.addressList = [];
        this.clearSelection();
      } else if (val === 'mpan') {
        this.meterForm.get('postcodeSearch')?.disable();
        this.meterForm.get('fileUpload')?.disable();
        this.meterForm.get('mpanSearch')?.reset();
        this.meterForm.get('mpanSearch')?.enable();
        this.clearSelection();
      } else if (val === 'file') {
        this.meterForm.get('postcodeSearch')?.disable();
        this.meterForm.get('mpanSearch')?.disable();
        this.meterForm.get('fileUpload')?.reset();
        this.meterForm.get('fileUpload')?.enable();
        this.clearSelection();
      }
    });

    // Watch for file upload
    this.meterForm.get('fileUpload.file')?.valueChanges.subscribe(val => {
      if (val) {
        this.selectedFile = val;
        this.onChange(this.selectedFile);
        this.onTouched();
        console.log('File uploaded:', val);
      }
    });
  }

  searchPostcode() {
    const control = this.meterForm.get('postcodeSearch.postcode');
    control?.setAsyncValidators(this.postcodeValidator());
    control?.updateValueAndValidity();
  }

  searchMPAN() {
    const control = this.meterForm.get('mpanSearch.mpan');
    control?.setAsyncValidators(this.mpanValidator());
    control?.updateValueAndValidity();
  }

  private postcodeValidator() {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      this.addressList = [];
      return this.regService.getAddressLine(control.value).pipe(
        tap((response) => {
          this.addressList = response.data;
        }),
        map(response => {
          return response ? null : { invalidValue: true, errorMessage: 'Unable to fetch addresses' };
        }),
        catchError((error) => {
          let errorMessage = 'Unable to fetch addresses. Please try again';
          if (error?.error?.code === 400 && error?.error?.message[0]?.message === 'invalid postcode') {
            errorMessage = 'Invalid post code';
          }
          console.error('Error fetching address:', error);
          return of({ invalidValue: true, errorMessage });
        }),
        finalize(() => {
          control.clearAsyncValidators();
        })
      );
    };
  }

  private mpanValidator() {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      if (control.value) {
        this.fetchingMeterDetails = true;
        return this.regService.getMeterDetailsByMPAN(control.value).pipe(
          tap((response) => {
            this.fetchingMeterDetails = false;
            if (response.data && response.data.length > 0) {
              const meter = response.data[0];
              if (this.isMeterAllowed(meter)) {
                this.addMeter(meter);
              }
            }
          }),
          map(response => {
            if (response.data && response.data.length > 0) {
              const meter = response.data[0];
              if (!this.isMeterAllowed(meter)) {
                return { invalidValue: true, errorMessage: 'Only half hourly meter and Smart meter are allowed' };
              }
            }
            return response ? null : { invalidValue: true, errorMessage: 'Unable to fetch meter details' };
          }),
          catchError((error) => {
            this.fetchingMeterDetails = false;
            let errorMessage = 'Unable to fetch meter details. Please try again';
            if (error?.error?.code === 404) {
              errorMessage = 'MPAN Not recognized';
            }
            return of({ invalidValue: true, errorMessage });
          }),
          finalize(() => {
            control.clearAsyncValidators();
          })
        );
      } else {
        return of(null);
      }
    };
  }

  onAddressSelected() {
    const addressId = this.meterForm.get('postcodeSearch.address')?.value;
    if (addressId) {
      this.fetchingMeterDetails = true;
      this.regService.getMeterDetailsByAddressId(addressId).subscribe({
        next: (response) => {
          this.fetchingMeterDetails = false;
          if (response.data && response.data.length > 0) {
            const meter = response.data[0];
            if (this.isMeterAllowed(meter)) {
              this.addMeter(meter);
            }
          }
        },
        error: (error) => {
          this.fetchingMeterDetails = false;
          console.error('Error fetching meter details:', error);
        }
      });
    }
  }

  private addMeter(meter: MeterDetails) {
    // Check if meter already exists
    const exists = this.selectedMeters.some(m => m.mpanCore === meter.mpanCore);
    if (!exists) {
      if (this.multiple) {
        this.selectedMeters.push(meter);
      } else {
        this.selectedMeters = [meter];
      }
      this.onChange(this.selectedMeters);
      this.onTouched();
      this.scrollToBottom();
    }
  }

  removeMeter(index: number) {
    this.selectedMeters.splice(index, 1);
    this.onChange(this.selectedMeters.length > 0 ? this.selectedMeters : null);
    this.onTouched();
  }

  private clearSelection() {
    this.selectedMeters = [];
    this.selectedFile = null;
    this.onChange(null);
  }

  private isMeterAllowed(meterDetails: MeterDetails): boolean {
    if (!meterDetails) return false;

    const { profileClass, meterType, mpanTopLine } = meterDetails;

    if (profileClass === '00') return true;

    if (meterType) {
      const firstChar = meterType.charAt(0).toLowerCase();
      if (firstChar === 's' || meterType.toLowerCase() === 'h' || meterType.toLowerCase() === 'hh') {
        return true;
      }
    }

    return mpanTopLine?.startsWith('00') || false;
  }

  isSmartMeter(meterDetails: MeterDetails): boolean {
    return meterDetails?.meterType?.toLowerCase().startsWith('s');
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: 'smooth'
      });
    }, 100);
  }

  // ControlValueAccessor implementation
  writeValue(value: MeterDetails[] | File | null): void {
    if (value instanceof File) {
      this.selectedFile = value;
      this.selectedMeters = [];
    } else if (Array.isArray(value)) {
      this.selectedMeters = value || [];
      this.selectedFile = null;
    } else {
      this.selectedMeters = [];
      this.selectedFile = null;
    }
  }

  registerOnChange(fn: (value: MeterDetails[] | File | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.meterForm.disable();
    } else {
      this.meterForm.enable();
    }
  }
}
